// Invoice correction request service — orchestrates the correction-request email
// to the invoice issuer:
//   • prepare()  builds the email *context* from invoices.reconcile_report and
//                renders it through the shared EmailService + the
//                'invoice_correction_request' template (no bespoke body builder).
//   • send()     sends via the Google edge fn (logged against invoiceId, NOT a
//                transaction), then flips review_status to 'request_correction'.
//
// The body is a Handlebars template (template_configs) — this service only
// assembles the line list; EmailService owns rendering. Lives in
// shared/services/invoice/ so it stays isomorphic with the rest of the invoice
// services.

import type { GoogleEdgeFnClient, SentEmail } from '../edgeFn/GoogleEdgeFnClient.ts';
import type { EmailService, PreparedEmail } from '../email/EmailService.ts';
import { formatDate, formatWeight } from '../email/helpers.ts';
import type { InvoiceDao } from './InvoiceDao.ts';
import type { TransactionDao } from '../transaction/TransactionDao.ts';
import type { InvoiceWithRelations } from '../../types/invoice.ts';
import type { PricingSide } from '../../types.ts';
import { SIDE_COLUMNS } from '../../sideFields.ts';

// ── Params ──────────────────────────────────────────────────────────────

export interface CorrectionRequestParams {
  /** Invoice to request a correction for — email logged here. */
  invoiceId: number;
  /** Email subject line. */
  subject: string;
  /** Final HTML body (template-rendered, possibly edited by the operator). */
  htmlBody: string;
  /** Primary recipients (invoicing-purpose contacts of the seller). */
  recipients: string[];
  /** Optional CC addresses added by the operator. */
  cc: string[];
}

/** Single correction line as the template's `{{#each lines}}` expects it. */
export interface CorrectionEmailLine {
  referenceNumber: string;
  loadDate: string | null;
  unloadDate: string | null;
  weight: string | null;
  priceLabel: string | null;
  hasMismatches: boolean;
  mismatches: { label: string; theirs: string; ours: string }[];
}

// ── Field / price formatting ─────────────────────────────────────────────

/** Technical reconcile field → Polish label shown in the email mismatch table. */
const CORRECTION_FIELD_LABELS: Record<string, string> = {
  net_amount: 'Wartość netto',
  gross_amount: 'Wartość brutto',
  unit_price: 'Cena jednostkowa',
  weight: 'Waga',
  vat_rate: 'Stawka VAT',
  vat_amount: 'Kwota VAT',
};

function correctionFieldLabel(field: string): string {
  return CORRECTION_FIELD_LABELS[field] ?? field;
}

/** "50,00 PLN/t" for per-ton, "500,00 PLN (ryczałt)" for flat rate. */
function formatCorrectionPrice(price: number, priceType: string | null | undefined, currency: string): string {
  const amount = `${price.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`.trim();
  if (priceType === 'flat_rate') return `${amount} (ryczałt)`;
  if (priceType === 'per_ton') return `${amount}/t`;
  return amount;
}

/** Numeric mismatch values get pl-PL 2-decimal formatting; non-numeric pass through. */
function formatMismatchValue(value: number | string): string {
  return typeof value === 'number'
    ? value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(value);
}

// ── Service ─────────────────────────────────────────────────────────────

export class InvoiceCorrectionRequestService {
  constructor(
    private readonly googleClient: GoogleEdgeFnClient,
    private readonly invoiceDao: InvoiceDao,
    // Used only by prepare(); optional so the send-only call-sites / tests can
    // construct with just the transport + DAO.
    private readonly emailService?: EmailService,
    private readonly transactionDao?: TransactionDao,
  ) {}

  /**
   * Build the correction-request email for every line the reconcile report
   * flagged with suggested_action === 'request_correction', enriched with the
   * near-miss transaction's dates / weight / price, and render it through the
   * shared EmailService template.
   */
  async prepare(invoice: InvoiceWithRelations, opts: { userEmail?: string } = {}): Promise<PreparedEmail> {
    if (!this.emailService || !this.transactionDao) {
      throw new Error('InvoiceCorrectionRequestService.prepare requires emailService + transactionDao');
    }

    const reportLines = (invoice.reconcile_report?.line_items ?? [])
      .filter((li) => li.suggested_action === 'request_correction');

    // Each request_correction line carries its transaction either as a firm
    // `matched_candidate` (P1 link + correction-required) or, when no link was
    // emitted, as a `near_miss` (P1 verify hard-failed). Prefer the firm link.
    const primaryTx = (li: typeof reportLines[number]) => {
      const mc = li.matched_candidates?.[0];
      if (mc && mc.type === 'transaction') return mc;
      const nm = li.near_misses?.[0];
      if (nm && nm.type === 'transaction') return nm;
      return undefined;
    };

    // Enrich each line with its primary transaction (dates / weight / price).
    const txIds = [...new Set(
      reportLines
        .map((li) => primaryTx(li)?.entity_id)
        .filter((id): id is number => id != null),
    )];
    const txRows = txIds.length ? await this.transactionDao.getByIds(txIds) : [];
    const txById = new Map(txRows.map((tx) => [tx.id, tx as unknown as Record<string, unknown>]));

    const lines: CorrectionEmailLine[] = reportLines.map((li) => {
      const nm = primaryTx(li);
      const tx = nm && nm.type === 'transaction' && nm.entity_id != null ? txById.get(nm.entity_id) : undefined;
      const side: PricingSide = (nm?.side as PricingSide) === 'load' ? 'load' : 'unload';
      const cols = SIDE_COLUMNS[side];

      const price = tx?.[cols.price] as number | null | undefined;
      const priceType = tx?.[cols.priceType] as string | null | undefined;
      const currency = (tx?.[cols.currency] as string | null | undefined) || invoice.currency || '';
      const weightVal = (tx?.[cols.weight] as number | null | undefined) ?? nm?.weight;
      const loadRaw = tx?.['load_date'] as string | null | undefined;
      const unloadRaw = (tx?.['unload_date'] as string | null | undefined) ?? nm?.date;

      const mismatches = (li.field_comparison ?? []).map((fc) => ({
        label: correctionFieldLabel(fc.field),
        theirs: formatMismatchValue(fc.actual),
        ours: formatMismatchValue(fc.expected),
      }));

      return {
        referenceNumber: (tx?.['reference_number'] as string | undefined) || nm?.reference_number || '',
        loadDate: loadRaw ? formatDate(loadRaw) : null,
        unloadDate: unloadRaw ? formatDate(unloadRaw) : null,
        weight: weightVal != null ? formatWeight(weightVal) : null,
        priceLabel: price != null ? formatCorrectionPrice(price, priceType, currency) : null,
        hasMismatches: mismatches.length > 0,
        mismatches,
      };
    });

    const recipients = (invoice.seller_contacts?.contacts ?? [])
      .filter((c) => !c.is_deleted && !!c.email && c.purposes.includes('invoicing'))
      .map((c) => c.email as string);

    return this.emailService.prepareWithContext(
      'invoice_correction_request',
      {
        invoiceNumber: invoice.invoice_number ?? `#${invoice.id}`,
        lines,
        emailRecipients: recipients,
      },
      { userEmail: opts.userEmail },
    );
  }

  /**
   * Send the correction-request email (logged against invoiceId), then set the
   * invoice's review_status to 'request_correction'.
   *
   * Order matters: if the send throws, the status is NOT flipped.
   */
  async send(params: CorrectionRequestParams): Promise<SentEmail> {
    const result = await this.googleClient.sendEmail({
      invoiceId: params.invoiceId,
      emailType: 'invoice_correction_request',
      subject: params.subject,
      htmlBody: params.htmlBody,
      recipients: params.recipients,
      cc: params.cc,
      attachments: [],
      isDraft: false,
    });

    // Flip only after a confirmed send — DAO enforces no raw Supabase queries here.
    await this.invoiceDao.updateStatus(params.invoiceId, { review_status: 'request_correction' });

    return result;
  }
}
